import gql from "graphql-tag";
import { RegistrationEntity } from "Models/Entities";
import { referrerBenefitType, referrerBusinessType, registrationStatus } from "Models/Enums";
import { store } from "Models/Store";
import moment from "moment";
import { buildSearchConditions } from "Util/GraphQLUtils";
import { IWhereCondition } from "Views/Components/ModelCollection/ModelQuery";
import { EntityControllerData } from "./shared";

export const getReferralPartners = async (
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	status: registrationStatus | 'ALL',
	type: referrerBusinessType | 'ALL',
	benefitType: referrerBenefitType | 'ALL',
	dateRange: string,
	pagination?: number,
) => {
	// If there is a search included, create the search param for the query
	const comparison = 'INVARIANT_CULTURE_IGNORE_CASE';
	let searchConditions: IWhereCondition<string>[] = [];
	if (!!search) {
		searchConditions = buildSearchConditions(['organisationName', 'userEmail'], search, comparison);
	}

	// Setup status filter
	const statusConditions: IWhereCondition<string>[] = [];
	if (!!status && status !== 'ALL') {
		statusConditions.push({
			comparison: 'equal', path: 'referralStatus', value: status,
		});
	}

	// Setup type filter
	const typeConditions: IWhereCondition<string>[] = [];
	if (!!type && type !== 'ALL') {
		typeConditions.push({
			comparison: 'equal', path: 'createdOrganisation.referrerBusinessType', value: type,
		}, {
			comparison: 'contains', path: 'referralData', value: `"referrerBusinessType":"${type}"`,
		});
	}

	// Setup referrer benefit type filter
	const benefitTypeConditions: IWhereCondition<string>[] = [];
	if (!!benefitType && benefitType !== 'ALL') {
		benefitTypeConditions.push({
			comparison: 'equal',
			path: 'createdOrganisation.referrerBenefitType',
			value: benefitType,
		}, {
			comparison: 'contains',
			path: 'referralData',
			value: `"referrerBenefitType":"${benefitType}"`,
		});
	}

	// Setup referrer start / end date type filter
	const startDateConditions: IWhereCondition<string>[] = [];
	const endDateConditions: IWhereCondition<string>[] = [];

	if (dateRange.length > 0) {
		startDateConditions.push({
			path: 'startDate',
			value: [moment(dateRange[0]).format('YYYY-MM-DD')],
			comparison: 'greaterThanOrEqual',
		});

		endDateConditions.push({
			path: 'startDate',
			value: [moment(dateRange[1]).format('YYYY-MM-DD')],
			comparison: 'lessThanOrEqual',
		});
	}

	// Aggregate conditions into single nested list
	const allConditions: IWhereCondition<string>[][] = [];

	// Only include Referral Registrations
	allConditions.push([
		{
			path: 'status', comparison: 'equal', value: 'CREATED',
		},
		// OR
		{
			path: 'referralStatus', comparison: 'equal', value: 'CREATED', negate: true,
		},
	]);

	if (searchConditions.length > 0) {
		allConditions.push(searchConditions);
	}
	if (statusConditions.length > 0) {
		allConditions.push(statusConditions);
	}
	if (typeConditions.length > 0) {
		allConditions.push(typeConditions);
	}
	if (benefitTypeConditions.length > 0) {
		allConditions.push(benefitTypeConditions);
	}
	if (startDateConditions.length > 0) {
		allConditions.push(startDateConditions);
	}
	if (endDateConditions.length > 0) {
		allConditions.push(endDateConditions);
	}

	const referralPartnersCondition: IWhereCondition<string>[] = [];
	referralPartnersCondition.push({
		comparison: 'equal', path: 'referralStatus', value: ['Created'], negate: true,
	});

	referralPartnersCondition.push({
		comparison: 'equal', path: 'createdOrganisation.isReferralPartner', value: ['true'],
	});

	allConditions.push(referralPartnersCondition);

	// Add sorting
	const orderBy: {path: string, descending: boolean}[] = [];
	orderBy.push({ path: sortColumn, descending: sortDescending });

	const query = gql`
		query registrationEntitys(
			$orderBy: [OrderByGraph],
			$take: Int,
			$skip: Int,
			$conditions: [[WhereExpressionGraph]]
		) {
			registrationEntitys(conditions: $conditions, skip: $skip, take:$take, orderBy: $orderBy) {
				id
				organisationName
				userEmail
				created
				startDate
				registrationData
				createdOrganisation {
					id
					isReferralPartner
					referrerBusinessType
					primaryBusinessEntity {
						id
						name
					}
					referrerBenefitType
					referrerCommissionPercent

				}
				referralStatus
				referralData
				createdUser {
					email
				}
			}
			countRegistrationEntitys(conditions: $conditions) {
				number
			}
		}
	`;

	const { data } = await store.apolloClient.query({
		query: query,
		fetchPolicy: 'network-only',
		variables: {
			orderBy: orderBy,
			take: !!pagination ? pagination : null,
			skip: !!pagination ? pagination * (page || 0) : null,
			conditions: allConditions,
		},
	});

	const response: EntityControllerData<RegistrationEntity> = {
		data: data.registrationEntitys?.map((registration: any) => new RegistrationEntity(registration)) || [],
		count: data.countRegistrationEntitys?.number || 0,
	}

	return response;
};
